import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';

interface Props {
  profilePhotoPreview: string;
  backgroundPhotoPreview: string;
  onPhotoCapture: (type: 'profile' | 'background', source: 'camera' | 'gallery') => Promise<void>;
}

const PhotoUpload: React.FC<Props> = ({
  profilePhotoPreview,
  backgroundPhotoPreview,
  onPhotoCapture,
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={0}
      className="glass-effect"
      sx={{
        p: 3,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="h6"
        className="animated-gradient-text"
        gutterBottom
        sx={{
          mb: 4,
          fontFamily: 'Poppins, sans-serif',
          textAlign: 'center'
        }}
      >
        {t('registration.photos.photoUploadHelp')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Profile Photo Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              border: 4,
              borderColor: 'background.paper',
              backgroundColor: 'primary.light',
              boxShadow: 3,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            {profilePhotoPreview ? (
              <Box
                component="img"
                src={profilePhotoPreview}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <PhotoCamera sx={{ fontSize: 40, color: 'white' }} />
            )}
          </Box>
          
          <Typography variant="subtitle1" fontWeight="medium">
            {t('profile.profile_photo_alt')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="button"
              variant="contained"
              startIcon={<PhotoCamera />}
              onClick={(e) => {
                e.preventDefault();
                onPhotoCapture('profile', 'camera');
              }}
              sx={{ borderRadius: '20px' }}
            >
              {t('registration.photos.takePhoto')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={(e) => {
                e.preventDefault();
                onPhotoCapture('profile', 'gallery');
              }}
              sx={{ borderRadius: '20px' }}
            >
              {t('registration.photos.gallery')}
            </Button>
          </Box>
        </Box>

        {/* Background Photo Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <Box
            sx={{
              width: '100%',
              height: '120px',
              borderRadius: 2,
              backgroundColor: 'primary.light',
              opacity: backgroundPhotoPreview ? 1 : 0.7,
              backgroundImage: `url(${backgroundPhotoPreview})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 2
            }}
          >
            {!backgroundPhotoPreview && (
              <PhotoCamera sx={{ fontSize: 40, color: 'white' }} />
            )}
          </Box>
          
          <Typography variant="subtitle1" fontWeight="medium">
            {t('registration.photos.coverPhoto')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="button"
              variant="contained"
              startIcon={<PhotoCamera />}
              onClick={(e) => {
                e.preventDefault();
                onPhotoCapture('background', 'camera');
              }}
              sx={{ borderRadius: '20px' }}
            >
              {t('registration.photos.takePhoto')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={(e) => {
                e.preventDefault();
                onPhotoCapture('background', 'gallery');
              }}
              sx={{ borderRadius: '20px' }}
            >
              {t('registration.photos.gallery')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PhotoUpload;
